import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { DefaultValues, FieldValues, UseFormProps } from "react-hook-form";
import React from "react";
import { FormSubmitResult } from "src/components/common/forms/types.ts";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { AavoFormOnSubmitFunc } from "src/components/common/forms/useAavoForm/types.ts";
import { AavoForm, AavoFormContentParams, AavoFormProps } from "src/components/common/forms/AavoForm.tsx";

export interface AsyncFormProps<TData, TFieldValues extends FieldValues, TResult>
	extends Omit<
		AavoFormProps<TFieldValues, TResult>,
		| "defaultValues"
		| "useFormProps"
		| "render"
		| "submit"
		| "footerExtraComponents"
		| "columns"
		| "disableSubmit"
		| "hideSubmit"
	> {
	fetch: () => Promise<TData>;
	getDefaultValues: (data: TData) => DefaultValues<TFieldValues>;
	getUseFormProps?: (data: TData) => Omit<UseFormProps<TFieldValues>, "defaultValues">;
	submit: AsyncFormSubmitFunc<TData, TFieldValues, TResult>;
	render: (props: AsyncFormContentParams<TData, TFieldValues>) => React.ReactNode;
	footerExtraComponents?: (
		formContentParams: AsyncFormContentParams<TData, TFieldValues>,
		triggerSubmit: (submitFunc?: AsyncFormSubmitFunc<TData, TFieldValues, TResult>) => Promise<void>,
	) => React.ReactNode;
	columns?: number | ((data: TData) => number);
	disableSubmit?: boolean | ((data: TData) => boolean);
	hideSubmit?: boolean | ((data: TData) => boolean);
}

export interface AsyncFormContentParams<TData, TFieldValues extends FieldValues>
	extends AavoFormContentParams<TFieldValues> {
	data: TData;
}

type AsyncFormSubmitFunc<TData, TFieldValues extends object, TResult> = (
	values: TFieldValues,
	props: AsyncFormContentParams<TData, TFieldValues>,
) => FormSubmitResult<TResult> | Promise<FormSubmitResult<TResult>>;

export const AsyncForm = <TData, TFieldValues extends FieldValues, TResult>({
	fetch,
	getDefaultValues: getDefaultValuesProp,
	getUseFormProps,
	render,
	submit,
	footerExtraComponents,
	columns: columnsProp,
	disableSubmit: disableSubmitProp,
	hideSubmit: hideSubmitProp,
	...other
}: AsyncFormProps<TData, TFieldValues, TResult>) => {
	const { logErrorAndShowOnDialog } = useErrorDialog();

	return (
		<AsyncFetchRender
			fetch={fetch}
			content={(data) => {
				return (
					<AavoForm
						defaultValues={getDefaultValues(data)}
						useFormProps={getUseFormProps?.(data)}
						render={(params) => render(convertFormContentParamsToAsync(params))}
						submit={convertAsyncSubmitFuncToSync(submit)}
						disableSubmit={
							typeof disableSubmitProp === "function" ? disableSubmitProp(data) : disableSubmitProp
						}
						hideSubmit={typeof hideSubmitProp === "function" ? hideSubmitProp(data) : hideSubmitProp}
						columns={typeof columnsProp === "function" ? columnsProp(data) : columnsProp}
						footerExtraComponents={
							footerExtraComponents === undefined ? undefined : (
								(aavoFormContentParams, triggerSubmit) =>
									footerExtraComponents(
										convertFormContentParamsToAsync(aavoFormContentParams),
										(onSubmit) => triggerSubmit(convertAsyncSubmitFuncToSync(onSubmit ?? submit)),
									)
							)
						}
						{...other}
					/>
				);

				function convertFormContentParamsToAsync(
					aavoFormContentParams: AavoFormContentParams<TFieldValues>,
				): AsyncFormContentParams<TData, TFieldValues> {
					return {
						...aavoFormContentParams,
						data: data,
					};
				}

				function convertAsyncSubmitFuncToSync(
					asyncSubmit: AsyncFormSubmitFunc<TData, TFieldValues, TResult>,
				): AavoFormOnSubmitFunc<TFieldValues, TResult> {
					return (values, props) => asyncSubmit(values, { ...props, data });
				}
			}}
		/>
	);

	function getDefaultValues(data: TData) {
		try {
			return getDefaultValuesProp(data);
		} catch (e) {
			logErrorAndShowOnDialog(e);
		}
	}
};
